import { useState, useRef, useEffect } from 'react';

interface HelpTipProps {
  tips: string[];
}

export function HelpTip({ tips }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const POPOVER_WIDTH = 288; // w-72
  const EDGE_PADDING = 8;

  const updatePos = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const idealLeft = r.left + r.width / 2;
      const minLeft = EDGE_PADDING + POPOVER_WIDTH / 2;
      const maxLeft = window.innerWidth - POPOVER_WIDTH / 2 - EDGE_PADDING;
      const clampedLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));
      setPos({ top: r.bottom + 8, left: clampedLeft });
    }
  };

  useEffect(() => { if (open) updatePos(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <span className="inline-block">
      <button
        ref={btnRef}
        onClick={() => { updatePos(); setOpen(!open); }}
        aria-expanded={open}
        aria-haspopup="true"
        className="ml-1 p-2.5 rounded-full hover:bg-brand-50 transition-colors inline-flex items-center justify-center"
        title="Ayuda"
      >
        <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 text-xs font-bold inline-flex items-center justify-center">?</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div role="tooltip" className="fixed z-[110] w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-sm" style={{ top: pos.top, left: pos.left, transform: 'translateX(-50%)' }}>
            <h4 className="font-semibold text-slate-700 mb-2">💡 Tips</h4>
            <ul className="space-y-1.5 text-slate-600">
              {tips.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-brand-500 shrink-0">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </span>
  );
}
